/**
 * Mid America Mortgage — "Condo Questionnaire, 2-4 Unit Projects".
 *
 * The second bank questionnaire (3-Sep-2026): fifteen questions over one page,
 * a fillable AcroForm of 55 fields (17 Yes/No checkbox PAIRS — the PDF has no
 * radio groups, each Yes and each No is its own checkbox whose on-state is
 * named "Yes"). Page 2 of the official file is the bank's answer key and has
 * no fields.
 *
 * Field NAMES are the shared knowledge pool (see src/forms/lender/
 * projectDetails.mjs): a name the 1076 also uses reads the same stored answer.
 * Only what this bank alone asks gets a new name.
 *
 * The `pdf` shapes here:
 *   "Field Name"                       a text field, written as-is
 *   { checks: { Yes: "A", No: "B" } }  a checkbox pair; the stored value picks
 *                                      which box is ticked
 *   { checks: …, yesWhen: "nonzero" }  the stored value is a COUNT; any number
 *                                      above zero ticks Yes, zero ticks No
 */

export const SYSTEM = "system";
export const OPERATIONS = "operations";
export const REQUESTOR = "requestor";

export const PDF_FILE = "mid-america-condo-2-4.pdf";

/** Nothing on this form comes from the order context (no return-by line). */
export const PDF_FROM_CONTEXT = {};

const YES_NO = ["Yes", "No"];

/** Checkbox pair n: the form's boxes run in question order, Yes first. */
const pair = (yes, no) => ({ checks: { Yes: `CheckBox${yes}`, No: `CheckBox${no}` } });

export const QUESTIONNAIRE = {
  documentTypeName: "Questionnaire – Mid America",
  formName: "Condo Questionnaire – 2-4 Unit Projects (Mid America Mortgage)",
  steps: [
    // -----------------------------------------------------------------------
    {
      title: "Loan",
      sections: [
        {
          title: "Loan",
          subtitle: "The seller and loan this questionnaire is for. Filled in by whoever places the order.",
          fields: [
            { label: "Lender name", name: "lender_name", type: "text", by: REQUESTOR, pdf: "Seller Name" },
            { label: "Mid America Seller ID # (optional — leave blank if you don’t have one)", name: "lender_seller_id", type: "text", by: REQUESTOR, pdf: "Seller ID #" },
            { label: "Loan number (optional)", name: "loan_number", type: "text", by: REQUESTOR, pdf: "Loan #" },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Project",
      sections: [
        {
          title: "Property",
          fields: [
            { label: "Project name", name: "association_name", type: "text", by: SYSTEM, pdf: "Property Name" },
            { label: "Property address", name: "association_physical_address", type: "text", by: SYSTEM, pdf: "Property Address" },
            { label: "Borrower name", name: "borrower_name", type: "text", by: SYSTEM, pdf: "Borrower Name" },
            { label: "Phase", name: "phase_number", type: "text", by: OPERATIONS, pdf: "Phase" },
          ],
        },
        {
          title: "Project Eligibility",
          fields: [
            { label: "Are all the Common Areas and facilities complete?", name: "completion_100_percent", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(1, 2) },
            { label: "Does any single entity own more than one unit?", name: "own_multi_unit_owner", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(3, 4) },
            { label: "If yes, how many units does each entity own?", name: "own_multi_1_units", type: "text", by: OPERATIONS, pdf: "# owned" },
            { label: "Are there any pending special assessments?", name: "bs_current_special_assessments", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(5, 6) },
            { label: "If yes, explain", name: "bs_current_sa_purpose", type: "text", by: OPERATIONS, pdf: "pending special assessments" },
            { label: "Is the HOA or unit owners involved in any litigation, arbitration, mediation or other dispute resolution process?", name: "fin_litigation", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(7, 8) },
            { label: "To the best of your knowledge, are there any adverse environmental factors affecting the project as a whole or the individual units?", name: "elig_environmental_factors", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(9, 10) },
            { label: "How many units are 30 days or more delinquent in their HOA dues?", name: "fin_delinquent_30_days", type: "text", by: OPERATIONS, pdf: { ...pair(11, 12), yesWhen: "nonzero" } },
            { label: "Do the project legal documents include any restrictions on sale which would limit the free transferability of title? (i.e. Age Restrictions, First Right of Refusal, other deed/income restrictions)", name: "q8_deed_restrictions", type: "checkbox", by: OPERATIONS, pdf: pair(13, 14) },
            { label: "Is any part of the project used for commercial purposes?", name: "own_commercial_use", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(15, 16) },
            { label: "If yes, what percentage of square footage?", name: "own_commercial_1_pct", type: "text", by: OPERATIONS, pdf: "Percentage" },
            { label: "Describe the commercial space", name: "own_commercial_1_type", type: "text", by: OPERATIONS, pdf: "commercial space" },
          ],
        },
        {
          title: "Insurance",
          fields: [
            { label: "Is the HOA named insured on the master insurance policy?", name: "ins_hoa_named_insured", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(17, 18) },
            { label: "Are common elements/limited common elements insured to 100% replacement cost?", name: "ins_replacement_coverage", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(19, 20) },
            { label: "Coverage ($)", name: "ins_coverage_amount", type: "text", by: OPERATIONS, pdf: "Coverage" },
            { label: "Deductible ($)", name: "ins_deductible", type: "text", by: OPERATIONS, pdf: "Deductible" },
            { label: "Expiration date", name: "ins_policy_expiration", type: "date", by: OPERATIONS, pdf: "Expiration Date" },
            { label: "Are units or common improvements located in a flood zone?", name: "ins_flood_zone", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(21, 22) },
            { label: "If yes, is flood insurance in force?", name: "ins_flood_in_force", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(23, 24) },
            { label: "Does this cover at least 80% replacement?", name: "ins_flood_80pct", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(25, 26) },
            { label: "Or, is this the coverage maximum available per federal flood program?", name: "ins_flood_max_federal", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(27, 28) },
            { label: "Is the HOA insured for general liability?", name: "ins_general_liability", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(29, 30) },
            { label: "If yes, amount ($)", name: "ins_liability_per_occurrence", type: "text", by: OPERATIONS, pdf: "amount" },
            { label: "Does the HOA provide hazard insurance coverage for the interior (walls-in) of the units?", name: "ins_walls_in_coverage", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(31, 32) },
            { label: "Minimum number of days' written notice to the HOA before any substantial policy change or cancellation", name: "ins_notice_days", type: "text", by: OPERATIONS, pdf: "min #" },
          ],
        },
        {
          title: "Seller guide eligibility",
          fields: [
            { label: "Is the project an “ineligible project” in accordance with MAM and/or agency guidelines?", name: "mam_ineligible_project", type: "select", by: OPERATIONS, options: YES_NO, pdf: pair(33, 34), perOrder: true },
          ],
        },
      ],
    },
    // -----------------------------------------------------------------------
    {
      title: "Reviewer",
      sections: [
        {
          title: "Reviewer",
          subtitle: "Who completed the review. Pre-filled from the person processing the order.",
          fields: [
            { label: "Reviewer's name", name: "prep_name", type: "text", by: OPERATIONS, pdf: ["Reviewer's Name", "Reviewer's Signature"], perOrder: true },
            { label: "Position / title", name: "prep_title", type: "text", by: OPERATIONS, pdf: "Position/Title", perOrder: true },
            { label: "Phone", name: "prep_phone", type: "text", by: OPERATIONS, pdf: "Phone", perOrder: true },
            { label: "Date", name: "prep_date", type: "date", by: OPERATIONS, pdf: "Date", perOrder: true },
          ],
        },
      ],
    },
  ],
};

/** Every field, flattened, in the order they appear. */
export function allFields() {
  const out = [];
  QUESTIONNAIRE.steps.forEach((step, si) =>
    step.sections.forEach((section, ci) =>
      section.fields.forEach((field, fi) =>
        out.push({ ...field, stepIndex: si, sectionIndex: ci, fieldIndex: fi,
                   stepTitle: step.title, sectionTitle: section.title })
      )
    )
  );
  return out;
}

/** The association-level questions this form asks (all shared-pool names). */
export function projectDetailFields() {
  return allFields().filter((f) => f.by === OPERATIONS && !f.perOrder);
}

/** The PDF fields a form field writes to, always as a list. */
export function pdfTargets(field) {
  if (!field.pdf) return [];
  if (typeof field.pdf === "string") return [field.pdf];
  if (Array.isArray(field.pdf)) return field.pdf;
  if (field.pdf.checks) return [field.pdf.checks.Yes, field.pdf.checks.No];
  return [field.pdf.radio];
}

/** This form has no radio groups; kept for interface parity with the 1076. */
export function radioState(field, option) {
  if (!field.pdf || typeof field.pdf !== "object" || Array.isArray(field.pdf)) return null;
  if (!field.pdf.states) return null;
  return field.pdf.states[option] ?? option;
}
